import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getContracts, getStats, getCategories, runScan, getScanStatus, archiveContract, favouriteContract, fmtValue, fmtDate } from "@/lib/api";
import { ScoreBadge } from "@/components/ScoreBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { Button } from "@/components/ui/button";
import { RadarIcon, RefreshCw, TrendingUp, Database, Calendar, ArrowRight, MapPin, Building2, Star, Lightbulb, CheckCircle2, Clock, X, ArchiveRestore, Archive, AlertTriangle, Search, Hammer, GraduationCap, Layers } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ALL = "All";
const TYPES = ["All", "Core", "Possible"];
const STAGES = [["All", "All"], ["Live now", "live"], ["Upcoming", "upcoming"]];
const BUSINESSES = [
  { id: "all", label: "All Businesses", icon: Layers, sub: "Everything" },
  { id: "cph", label: "CPH Retrofit", icon: Hammer, sub: "Retrofit & works" },
  { id: "nts", label: "Nationwide Training", icon: GraduationCap, sub: "Training & skills" },
];
const SOURCE_META = {
  "Find a Tender": { label: "Find a Tender", cls: "bg-[#2F6DA3] text-white" },
  "Public Contracts Scotland": { label: "Scotland · PCS", cls: "bg-[#14304F] text-white" },
  "Contracts Finder": { label: "Contracts Finder", cls: "bg-zinc-100 text-zinc-500 border border-zinc-300" },
};

const StatCard = ({ icon: Icon, label, value, accent, onClick, active, testid }) => {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      data-testid={testid}
      type={onClick ? "button" : undefined}
      className={`w-full text-left border-2 border-zinc-950 p-4 bg-white transition-all duration-200 ${accent ? "shadow-[4px_4px_0px_0px_#2F6DA3]" : ""} ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_0px_#2F6DA3]" : ""} ${active ? "ring-2 ring-offset-2 ring-[#2F6DA3] -translate-y-0.5 shadow-[6px_6px_0px_0px_#2F6DA3]" : ""}`}
    >
      <div className="flex items-center gap-2 text-zinc-500 mb-2">
        <Icon size={15} strokeWidth={2.5} />
        <span className="text-[10px] font-mono uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tight">{value}</div>
      {onClick && (
        <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#2F6DA3] font-bold mt-1">
          {active ? "Showing · click to reset" : "Click to view →"}
        </div>
      )}
    </Comp>
  );
};

export default function Dashboard() {
  const [business, setBusiness] = useState("all");
  const [category, setCategory] = useState(CATEGORY_ALL);
  const [oppType, setOppType] = useState("All");
  const [area, setArea] = useState("All");
  const [stage, setStage] = useState("All");
  const [minScore, setMinScore] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [showFavourites, setShowFavourites] = useState(false);
  const [source, setSource] = useState("All");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: getCategories });
  const { data: stats } = useQuery({ queryKey: ["stats", business], queryFn: () => getStats({ business }) });
  const { data, isLoading } = useQuery({
    queryKey: ["contracts", business, category, minScore, oppType, area, stage, showArchived, showFavourites, source, debouncedSearch],
    queryFn: () => getContracts({ business, category, min_score: minScore, opportunity_type: oppType, area, stage, archived: showArchived, favourite: showFavourites, source, search: debouncedSearch, sort: "score" }),
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const startScan = useCallback(async () => {
    setScanning(true);
    setScanInfo(null);
    try {
      await runScan({ days_back: 21, max_score: 120 });
      pollRef.current = setInterval(async () => {
        const s = await getScanStatus();
        setScanInfo(s);
        if (s.status === "done") {
          clearInterval(pollRef.current);
          setScanning(false);
          toast.success(`Scan complete · ${s.scored} scored from ${s.relevant} relevant (${s.fetched} notices found)`);
          qc.invalidateQueries({ queryKey: ["contracts"] });
          qc.invalidateQueries({ queryKey: ["stats"] });
        }
      }, 3000);
    } catch (e) {
      setScanning(false);
      toast.error("Scan failed to start. Try again.");
    }
  }, [qc]);

  const contracts = data?.contracts || [];
  const categories = [CATEGORY_ALL, ...(cats?.categories || [])];
  const areas = ["All", ...(cats?.areas || []), "National"];

  const toggleArchive = async (e, c, archived) => {
    e.stopPropagation();
    qc.setQueriesData({ queryKey: ["contracts"] }, (old) =>
      old ? { ...old, contracts: old.contracts.filter((x) => x.id !== c.id), count: old.count - 1 } : old
    );
    try {
      await archiveContract(c.id, archived);
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(archived ? "Moved to archive" : "Restored to opportunities");
    } catch (err) {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      toast.error("Could not update. Try again.");
    }
  };

  const toggleFavourite = async (e, c) => {
    e.stopPropagation();
    const next = !c.favourite;
    qc.setQueriesData({ queryKey: ["contracts"] }, (old) => {
      if (!old) return old;
      if (showFavourites && !next) return { ...old, contracts: old.contracts.filter((x) => x.id !== c.id), count: old.count - 1 };
      return { ...old, contracts: old.contracts.map((x) => (x.id === c.id ? { ...x, favourite: next } : x)) };
    });
    try {
      await favouriteContract(c.id, next);
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      toast.error("Could not update favourite.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">UK & British Isles · Public Sector</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Contract Opportunities</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Live tenders scanned from UK Government Contracts Finder, AI-scored out of 5 against{" "}
            {business === "nts" ? "Nationwide Training Services' training capabilities" : business === "cph" ? "CPH Retrofit's works capabilities" : "both CPH Retrofit and Nationwide Training Services"}. Flagged as <strong>Core</strong> (deliver now) or <strong>Possible</strong> (achievable stretch).
          </p>
        </div>
        <Button
          data-testid="scan-button"
          onClick={startScan}
          disabled={scanning}
          className="bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider px-6 py-6 border-2 border-[#2F6DA3]"
        >
          {scanning ? <RefreshCw className="animate-spin mr-2" size={16} /> : <RadarIcon className="mr-2" size={16} />}
          {scanning ? "Scanning…" : "Scan New Contracts"}
        </Button>
      </div>

      {/* Business switcher — CPH Retrofit (works) vs Nationwide Training (training) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="business-switcher">
        {BUSINESSES.map((b) => {
          const Icon = b.icon;
          const activeB = business === b.id;
          const count = b.id === "all"
            ? (stats?.cph_count ?? 0) + (stats?.nts_count ?? 0)
            : b.id === "cph" ? stats?.cph_count : stats?.nts_count;
          return (
            <button
              key={b.id}
              data-testid={`business-${b.id}`}
              onClick={() => setBusiness(b.id)}
              className={`flex items-center gap-3 border-2 p-3.5 text-left transition-all duration-200 ${
                activeB
                  ? "border-[#2F6DA3] bg-[#2F6DA3] text-white shadow-[4px_4px_0px_0px_#14304F]"
                  : "border-zinc-300 bg-white hover:border-[#2F6DA3] hover:-translate-y-0.5"
              }`}
            >
              <Icon size={22} strokeWidth={2.2} className={activeB ? "text-white" : "text-[#2F6DA3]"} />
              <div className="min-w-0">
                <div className="font-display text-base font-bold tracking-tight leading-none">{b.label}</div>
                <div className={`text-[10px] font-mono uppercase tracking-wider mt-1 ${activeB ? "text-blue-100" : "text-zinc-400"}`}>
                  {b.sub}{count != null ? ` · ${count}` : ""}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="relative" data-testid="search-bar">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          data-testid="search-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by buyer, keyword or title — e.g. 'Saffron', 'PAS 2035', 'social housing'…"
          className="w-full border-2 border-zinc-950 bg-white pl-11 pr-11 py-3.5 text-sm font-medium placeholder:text-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_#2F6DA3] transition-shadow"
        />
        {search && (
          <button
            data-testid="search-clear"
            onClick={() => setSearch("")}
            title="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-900"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {scanning && (
        <div className="border-2 border-[#2F6DA3] bg-orange-50 p-4 text-sm" data-testid="scan-progress">
          <div className="flex items-center gap-2 font-bold">
            <RefreshCw className="animate-spin text-[#2F6DA3]" size={15} />
            {scanInfo?.status === "fetching" || !scanInfo ? "Fetching the latest UK tenders…" :
             scanInfo?.status === "scoring" ? `AI-scoring opportunities… ${scanInfo.scored_so_far || 0}/${scanInfo.to_score || "?"}` :
             "Finishing up…"}
          </div>
          {scanInfo?.relevant ? (
            <div className="text-xs text-zinc-600 mt-1 font-mono">
              {scanInfo.fetched} notices found · {scanInfo.relevant} relevant to your trades · scoring up to {scanInfo.to_score}. This runs in the background — you can keep using the app.
            </div>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={Database} label="Total Tracked" value={stats?.total ?? "—"} />
        <StatCard
          icon={TrendingUp}
          label="Top Picks (4-5)"
          value={stats?.top_count ?? "—"}
          accent
          testid="stat-top-picks"
          active={minScore >= 4}
          onClick={() => setMinScore(minScore >= 4 ? 1 : 4)}
        />
        <StatCard icon={CheckCircle2} label="Core Fit" value={stats?.core_count ?? 0} />
        <StatCard icon={Clock} label="Upcoming" value={stats?.upcoming_count ?? 0} />
        <StatCard icon={Calendar} label="Last Scan" value={stats?.last_scan ? fmtDate(stats.last_scan) : "Never"} />
      </div>

      {/* Stage: live vs upcoming */}
      <div className="border-2 border-[#2F6DA3] bg-white p-4 flex flex-wrap items-center gap-3" data-testid="stage-filter">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#2F6DA3] font-bold flex items-center gap-1"><Clock size={13} /> Tender stage</span>
        <div className="flex flex-wrap gap-2">
          {STAGES.map(([label, val]) => (
            <button
              key={val}
              data-testid={`stage-${val.toLowerCase()}`}
              onClick={() => setStage(val)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${
                stage === val ? "bg-[#2F6DA3] text-white border-[#2F6DA3]" : "bg-white border-zinc-300 hover:border-[#2F6DA3]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-400 ml-auto hidden md:block">Upcoming = published before tender opens — get ahead early.</span>
        <button
          data-testid="toggle-favourites"
          onClick={() => { setShowFavourites((v) => !v); if (!showFavourites) setShowArchived(false); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
            showFavourites ? "bg-amber-400 text-zinc-900 border-amber-400" : "bg-white border-zinc-300 hover:border-amber-400"
          }`}
        >
          <Star size={13} fill={showFavourites ? "currentColor" : "none"} /> {showFavourites ? "Viewing favourites" : `Favourites${stats?.favourite_count ? ` (${stats.favourite_count})` : ""}`}
        </button>
        <button
          data-testid="toggle-archived"
          onClick={() => { setShowArchived((v) => !v); if (!showArchived) setShowFavourites(false); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
            showArchived ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:border-zinc-900"
          }`}
        >
          <Archive size={13} /> {showArchived ? "Viewing archive" : `Archive${stats?.archived_count ? ` (${stats.archived_count})` : ""}`}
        </button>
      </div>

      {stats?.by_source && Object.keys(stats.by_source).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="source-coverage">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400">Filter by source</span>
          <button
            data-testid="source-all"
            onClick={() => setSource("All")}
            className={`px-2 py-1 text-[11px] font-bold border-2 transition-colors ${source === "All" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-300 hover:border-zinc-900"}`}
          >
            All sources
          </button>
          {Object.entries(stats.by_source).sort((a, b) => b[1] - a[1]).map(([src, n]) => {
            const sm = SOURCE_META[src] || SOURCE_META["Contracts Finder"];
            const active = source === src;
            return (
              <button
                key={src}
                data-testid={`source-filter-${src.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                onClick={() => setSource(active ? "All" : src)}
                className={`px-2 py-1 text-[11px] font-bold transition-all ${sm.cls} ${active ? "ring-2 ring-offset-1 ring-zinc-900" : "opacity-70 hover:opacity-100"}`}
              >
                {sm.label}: {n}
              </button>
            );
          })}
        </div>
      )}


      {/* Area filter */}
      <div className="border-2 border-[#14304F] bg-white p-4 flex flex-wrap items-center gap-3" data-testid="area-filter">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#14304F] font-bold flex items-center gap-1"><MapPin size={13} /> Area we work</span>
        <div className="flex flex-wrap gap-2">
          {areas.map((a) => (
            <button
              key={a}
              data-testid={`area-${a.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              onClick={() => setArea(a)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${
                area === a ? "bg-[#14304F] text-white border-[#14304F]" : "bg-white border-zinc-300 hover:border-[#14304F]"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="border-2 border-zinc-950 bg-white p-4 flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Type</span>
        <div className="flex gap-2">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`type-${t.toLowerCase()}`}
              onClick={() => setOppType(t)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${
                oppType === t ? (t === "Possible" ? "bg-[#4F9BDC] text-white border-[#4F9BDC]" : "bg-zinc-950 text-white border-zinc-950") : "bg-white border-zinc-300 hover:border-zinc-950"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="h-6 w-px bg-zinc-200 mx-1" />
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Category</span>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${
                category === c ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-300 hover:border-zinc-950"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Min Score</span>
          {[1, 3, 4, 5].map((s) => (
            <button
              key={s}
              data-testid={`minscore-${s}`}
              onClick={() => setMinScore(s)}
              className={`w-8 h-8 text-xs font-bold border-2 transition-colors ${
                minScore === s ? "bg-[#2F6DA3] text-white border-[#2F6DA3]" : "bg-white border-zinc-300 hover:border-zinc-950"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-center py-20 text-zinc-400 font-mono text-sm">Loading opportunities…</div>
      ) : contracts.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-white py-20 text-center" data-testid="empty-state">
          {showArchived ? (
            <>
              <p className="font-display text-2xl font-bold mb-2">Your archive is empty</p>
              <p className="text-sm text-zinc-500 mb-6">Contracts you dismiss with the ✕ will appear here, and can be restored any time.</p>
              <Button onClick={() => setShowArchived(false)} className="bg-zinc-950 text-white font-bold uppercase tracking-wider">
                Back to Opportunities
              </Button>
            </>
          ) : (
            <>
              <p className="font-display text-2xl font-bold mb-2">No contracts match these filters</p>
              <p className="text-sm text-zinc-500 mb-6">Try widening the filters, or run a scan to pull the latest UK opportunities.</p>
              <Button onClick={startScan} disabled={scanning} className="bg-zinc-950 text-white font-bold uppercase tracking-wider">
                <RadarIcon className="mr-2" size={16} /> Scan Now
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="contracts-list">
          <div className="text-xs font-mono text-zinc-500">{contracts.length} opportunities</div>
          {contracts.map((c, i) => {
            const possible = c.opportunity_type === "Possible";
            const upcoming = c.stage === "upcoming";
            const fav = c.favourite;
            return (
              <div
                key={c.id}
                data-testid={`contract-card-${c.id}`}
                onClick={() => navigate(`/contract/${c.id}`)}
                className={`group border-2 bg-white p-5 cursor-pointer fade-up hover:-translate-y-0.5 transition-all duration-200 ${
                  fav ? "border-l-[6px] border-l-amber-400 border-amber-300 hover:shadow-[5px_5px_0px_0px_#fbbf24]"
                  : possible ? "border-l-[6px] border-l-[#4F9BDC] border-zinc-200 hover:border-[#4F9BDC] hover:shadow-[5px_5px_0px_0px_#4F9BDC]" : "border-zinc-200 hover:border-zinc-950 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)]"
                }`}
                style={{ animationDelay: `${i * 25}ms` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <ScoreBadge score={c.ai_score} recommendation={c.ai_recommendation} />
                      {upcoming ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#2F6DA3] text-white px-2 py-1" data-testid="upcoming-tag">
                          <Clock size={11} /> Upcoming
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-1" data-testid="live-tag">
                          <RadarIcon size={11} /> Live
                        </span>
                      )}
                      <DeadlineBadge deadline={c.deadline} />
                      {(() => { const sm = SOURCE_META[c.source] || SOURCE_META["Contracts Finder"]; return (
                        <span data-testid={`source-${c.id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${sm.cls}`}>{sm.label}</span>
                      ); })()}
                      {possible ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#4F9BDC] text-white px-2 py-1" data-testid="possible-tag">
                          <Lightbulb size={11} /> Possible
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white px-2 py-1">
                          <CheckCircle2 size={11} /> Core
                        </span>
                      )}
                      {c.matched_areas?.map((a) => (
                        <span key={a} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#14304F] text-white px-2 py-1">
                          <MapPin size={10} /> {a}
                        </span>
                      ))}
                      {c.matched_categories?.slice(0, 3).map((m) => (
                        <span key={m} className="text-[10px] font-mono uppercase tracking-wider bg-zinc-100 px-2 py-1 text-zinc-600 border border-zinc-200">
                          {m}
                        </span>
                      ))}
                      {c.portal && c.portal_registered === false && (() => {
                        const d = c.deadline ? Math.ceil((new Date(c.deadline) - Date.now()) / 86400000) : null;
                        const urgent = d != null && d >= 0 && d <= 7;
                        return (
                          <span data-testid={`prereg-badge-${c.id}`} title={`You're not registered on ${c.portal} yet${d != null && d >= 0 ? ` — closes in ${d} day(s)` : ""}`} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${urgent ? "bg-red-600 text-white" : "bg-amber-400 text-amber-950"}`}>
                            <AlertTriangle size={10} /> Register: {c.portal.split(" (")[0]}{d != null && d >= 0 ? ` · ${d}d` : ""}
                          </span>
                        );
                      })()}
                    </div>
                    <h3 className={`font-display text-xl font-bold tracking-tight transition-colors ${possible ? "group-hover:text-[#4F9BDC]" : "group-hover:text-[#2F6DA3]"}`}>
                      {c.title}
                    </h3>
                    <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{c.ai_summary}</p>
                    {possible && c.entry_route ? (
                      <div className="mt-2 text-xs text-[#4F9BDC] bg-blue-50 border border-blue-100 px-3 py-2 flex items-start gap-2">
                        <Lightbulb size={13} className="mt-0.5 shrink-0" />
                        <span><strong>Route in:</strong> {c.entry_route}</span>
                      </div>
                    ) : null}
                    {c.readiness_gaps && c.readiness_gaps.length > 0 ? (
                      <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2" data-testid={`gap-hint-${c.id}`}>
                        <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" />
                        <span><strong>May need:</strong> {c.readiness_gaps.join(" · ")} <span className="text-amber-600">(check Readiness)</span></span>
                      </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-zinc-500 font-mono">
                      <span className="flex items-center gap-1"><Building2 size={12} /> {c.buyer_name || "—"}</span>
                      <span className="flex items-center gap-1"><MapPin size={12} /> {c.region || c.country || "UK"}</span>
                      <span className="flex items-center gap-1"><Calendar size={12} /> Closes {fmtDate(c.deadline)}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end">
                    <div className="flex items-center gap-1 mb-2">
                      {!showArchived && (
                        <button
                          data-testid={`favourite-${c.id}`}
                          onClick={(e) => toggleFavourite(e, c)}
                          title={fav ? "Unpin favourite" : "Pin to top — favourite"}
                          className={`p-1.5 border-2 transition-colors ${
                            fav ? "text-amber-500 border-amber-400 bg-amber-50" : "text-zinc-300 border-transparent hover:text-amber-500 hover:border-amber-300"
                          }`}
                        >
                          <Star size={16} fill={fav ? "currentColor" : "none"} strokeWidth={2.5} />
                        </button>
                      )}
                      {showArchived ? (
                        <button
                          data-testid={`restore-${c.id}`}
                          onClick={(e) => toggleArchive(e, c, false)}
                          title="Restore to opportunities"
                          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700 border-2 border-emerald-600 px-2 py-1 hover:bg-emerald-600 hover:text-white transition-colors"
                        >
                          <ArchiveRestore size={13} /> Restore
                        </button>
                      ) : (
                        <button
                          data-testid={`archive-${c.id}`}
                          onClick={(e) => toggleArchive(e, c, true)}
                          title="Not interested — move to archive"
                          className="p-1.5 text-zinc-300 hover:text-white hover:bg-zinc-900 border-2 border-transparent hover:border-zinc-900 transition-colors"
                        >
                          <X size={16} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                    <div className="font-display text-2xl font-extrabold tracking-tight">{fmtValue(c.value_amount)}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-3">Est. Value</div>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity ${possible ? "text-[#4F9BDC]" : "text-[#2F6DA3]"}`}>
                      Bid Writer <ArrowRight size={13} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
